import { z } from 'zod';
import { WidgetType, ZodBaseWidgetConfiguration, TimelineSlot } from '../../types';
import { PlaceExchangeWidget } from './PlaceExchangeWidget';
import {
    BaseThirdPartyAd,
    ValidAssetTypes,
    ZodBaseThirdPartyAdWidgetConfigurationDetails,
} from '../types';
import { AxiosError } from 'axios';

export type PlaceExchangeAd = BaseThirdPartyAd & {
    /**
     * A uniquely generated string contained withing an
     * ad response from Place Exchange.  It is used when
     * submitting  proof of play for a Place Exchange ad
     * to uniquely identify the ad request you are submitting
     * an ad for.
     */
    context: string;
};

/**
 * Schema mirroring the JSON response of place exchange's GET endpoint for ad requests
 * @see {@link https://developers.placeexchange.com/docs/api-publisher/1721385bb434e-get-ad-request}
 */
export const placeExchangeApiFetchAdsResponseSchema = z.object({
    creative: z.object({
        id: z.string(),
        type: z.union([z.literal('display'), z.literal('video')]),
        h: z.number().nullable(),
        w: z.number().nullable(),
        snapshots: z.array(
            z.object({
                h: z.number().nullable(),
                w: z.number().nullable(),
                iurl: z.string().nullable(),
                curl: z.string().optional(),
            })
        ),
        duration: z.number(),
    }),
    context: z.string(),
});

/**
 * Type representing data returned by PlaceExchange API after ad has been served
 */
export type PlaceExchangeApiFetchAdsResponse = z.infer<
    typeof placeExchangeApiFetchAdsResponseSchema
>;

/**
 * @example Sample of JSON PlaceExchange returns upon error.
 * ```json
 * {
 *   "message": "Auction is not configured",
 *   "errors": [
 *       "Auction must be configured on adrequest, adunit, network, or org.config"
 *   ]
 * }
 * ```
 */
export type PlaceExchangeError = AxiosError<{ message: string }>;

export const mappedPlaceExchangeAPITypes: { [key: string]: ValidAssetTypes } = {
    video: 'video',
    display: 'image',
};

export type PlaceExchangeWidgetTimelineSlotParams = {
    ad: PlaceExchangeAd;
};

export type PlaceExchangeWidgetTimelineSlot = TimelineSlot<
    PlaceExchangeWidgetTimelineSlotParams,
    PlaceExchangeWidget
>;

export const ZodPlaceExchangeWidgetConfigurationDetails =
    ZodBaseThirdPartyAdWidgetConfigurationDetails.extend({
        organization_id: z.string(),
    });

export type PlaceExchangeWidgetConfigurationDetails = z.infer<
    typeof ZodPlaceExchangeWidgetConfigurationDetails
>;

export const ZodPlaceExchangeWidgetConfiguration = ZodBaseWidgetConfiguration.extend({
    type: z.literal(WidgetType.PLACE_EXCHANGE),
    config: ZodPlaceExchangeWidgetConfigurationDetails,
});

export type PlaceExchangeWidgetConfiguration = z.infer<typeof ZodPlaceExchangeWidgetConfiguration>;
